import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
    Container,
    Paper,
    Box,
    Typography,
    Grid,
    Button,
    TextField,
    Card,
    CardContent,
    CardActions,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    FormControlLabel,
    Checkbox,
    IconButton,
    Tooltip,
    Switch,
    Alert,
    Chip,
} from '@mui/material';
import {
    Add as AddIcon,
    Delete as DeleteIcon,
    Edit as EditIcon,
} from '@mui/icons-material';
import { db } from '../firebase/config';
import { doc, getDoc, setDoc, updateDoc } from 'firebase/firestore';

export default function EvaluationFormBuilder() {
    const { formId } = useParams();
    const navigate = useNavigate();
    const [isPublished, setIsPublished] = useState(false);
    const [existingSubmission, setExistingSubmission] = useState(null);
    const [formTitle, setFormTitle] = useState('');
    const [formDescription, setFormDescription] = useState('');
    const [questions, setQuestions] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editingQuestionIndex, setEditingQuestionIndex] = useState(null);
    const [status, setStatus] = useState('inactive');
    const [jsonDialogOpen, setJsonDialogOpen] = useState(false);
    const [jsonInput, setJsonInput] = useState('');

    // ✅ NEW: Updated question structure with maxScore
    const [newQuestion, setNewQuestion] = useState({
        text: '',
        type: 'single-select',
        mandatory: true,
        maxScore: 10,  // ✅ NEW: Question max score
        options: [{ text: '', score: 5 }],  // ✅ Updated: Removed autoReject, kept score
    });

    useEffect(() => {
        if (formId && formId !== 'new') {
            fetchForm();
        }
    }, [formId]);

    const fetchForm = async () => {
        try {
            setLoading(true);
            const formDoc = await getDoc(doc(db, 'forms', formId));
            if (formDoc.exists()) {
                const data = formDoc.data();
                setFormTitle(data.title);
                setFormDescription(data.description);


                // ✅ Migrate old data format if needed
                const migratedQuestions = data.questions?.map(q => ({
                    ...q,
                    maxScore: q.maxScore || 10,
                    options: q.options?.map(opt => ({
                        text: opt.text,
                        score: opt.score || 0
                    })) || []
                })) || [];

                setQuestions(migratedQuestions);
                setStatus(data.status);
                setIsPublished(data.isPublished ?? false);
            }
        } catch (err) {
            console.error('Error fetching form:', err);
            setError('Failed to load form');
        } finally {
            setLoading(false);
        }
    };

    const handleSaveForm = async () => {
        try {
            if (!formTitle.trim()) {
                setError('Form title is required');
                return;
            }

            if (questions.length === 0) {
                setError('Add at least one question');
                return;
            }

            // Validate all questions have text
            if (questions.some(q => !q.text.trim())) {
                setError('All questions must have text');
                return;
            }

            // Validate all questions have at least one option
            if (questions.some(q => q.type !== 'paragraph' && (!q.options || q.options.length === 0))) {
                setError('All questions must have at least one option');
                return;
            }

            setLoading(true);

            const formData = {
                title: formTitle,
                description: formDescription,
                formType: 'evaluation',
                questions: questions,
                status: status,
                isLocked: false,
                isPublished: isPublished,
                createdAt: new Date(),
                updatedAt: new Date(),
            };

            if (formId && formId !== 'new') {
                await updateDoc(doc(db, 'forms', formId), formData);
            } else {
                const newFormId = `eval_${Date.now()}`;
                await setDoc(doc(db, 'forms', newFormId), {
                    ...formData,
                    createdAt: new Date(),
                });
            }

            setError('');
            alert('Form saved successfully!');
            navigate('/form-builder');
        } catch (err) {
            console.error('Error saving form:', err);
            setError('Failed to save form: ' + err.message);
        } finally {
            setLoading(false);
        }
    };

    const handleAddQuestion = () => {
        // Validate new question
        if (!newQuestion.text.trim()) {
            setError('Question text is required');
            return;
        }

        if (newQuestion.type !== 'paragraph' && (!newQuestion.options || newQuestion.options.length === 0)) {
            setError('Add at least one option');
            return;
        }

        if (newQuestion.type !== 'paragraph' && newQuestion.options.some(opt => !opt.text.trim())) {
            setError('All options must have text');
            return;
        }

        // ✅ Validate option scores
        if (newQuestion.type !== 'paragraph' && newQuestion.options.some(opt => opt.score === '' || opt.score === null)) {
            setError('All options must have a score');
            return;
        }

        if (editingQuestionIndex !== null) {
            const updated = [...questions];
            updated[editingQuestionIndex] = newQuestion;
            setQuestions(updated);
            // console.log('✅ Question updated');
        } else {
            setQuestions([...questions, newQuestion]);
            // console.log('✅ Question added');
        }

        setNewQuestion({
            text: '',
            type: 'single-select',
            mandatory: true,
            maxScore: 10,
            options: [{ text: '', score: 5 }],
        });
        setEditingQuestionIndex(null);
        setDialogOpen(false);
        setError('');
    };


    const handleEditQuestion = (index) => {
        setNewQuestion(questions[index]);
        setEditingQuestionIndex(index);
        setDialogOpen(true);
    };

    const handleDeleteQuestion = (index) => {
        if (window.confirm('Delete this question?')) {
            setQuestions(questions.filter((_, i) => i !== index));
            // console.log('✅ Question deleted');
        }
    };
    const handleImportJson = () => {
        try {
            const parsed = JSON.parse(jsonInput);

            // 🔒 BASIC STRUCTURE VALIDATION
            if (parsed.formType !== 'evaluation') {
                throw new Error('Invalid formType. Expected "evaluation".');
            }

            if (!Array.isArray(parsed.questions)) {
                throw new Error('Questions must be an array.');
            }

            // 🔒 QUESTION VALIDATION
            parsed.questions.forEach((q, index) => {
                if (!q.text || !q.type) {
                    throw new Error(`Question ${index + 1} is missing text or type`);
                }

                if (q.type !== 'paragraph' && !Array.isArray(q.options)) {
                    throw new Error(`Question ${index + 1} options invalid`);
                }

                if (q.options) {
                    q.options.forEach((opt, optIndex) => {
                        if (typeof opt.text !== 'string' || typeof opt.score !== 'number') {
                            throw new Error(
                                `Invalid option at Q${index + 1}, option ${optIndex + 1}`
                            );
                        }
                    });
                }
            });

            // ✅ APPLY DATA TO STATE
            setFormTitle(parsed.title || '');
            setFormDescription(parsed.description || '');
            setStatus(parsed.status || 'inactive');
            setIsPublished(parsed.isPublished ?? false);

            setQuestions(
                parsed.questions.map(q => ({
                    text: q.text,
                    type: q.type,
                    mandatory: q.mandatory ?? true,
                    maxScore: q.maxScore ?? 10,
                    options: q.options || [],
                }))
            );

            setJsonDialogOpen(false);
            setError('');

        } catch (err) {
            setError(`Invalid JSON: ${err.message}`);
        }
    };


    const handleAddOption = () => {
        setNewQuestion({
            ...newQuestion,
            options: [...newQuestion.options, { text: '', score: 5 }],
        });
    };

    // ✅ Updated: Handles both text and score fields
    const handleOptionChange = (optionIndex, field, value) => {
        const updated = [...newQuestion.options];

        if (field === 'score') {
            updated[optionIndex][field] = parseInt(value) || 0;
        } else {
            updated[optionIndex][field] = value;
        }

        setNewQuestion({ ...newQuestion, options: updated });
    };

    const handleDeleteOption = (optionIndex) => {
        if (newQuestion.options.length <= 1) {
            setError('At least one option is required');
            return;
        }

        setNewQuestion({
            ...newQuestion,
            options: newQuestion.options.filter((_, i) => i !== optionIndex),
        });
    };

    // ✅ Helper function to calculate total max score
    const calculateTotalMaxScore = () => {
        return questions.reduce((sum, q) => sum + (q.maxScore || 10), 0);
    };

    return (

        <Container maxWidth="lg" sx={{ py: 3 }}>
            {error && (
                <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
                    {error}
                </Alert>
            )}

            {/* Form Header */}
            <Paper sx={{ p: { xs: 2, sm: 3 }, mb: 3, borderRadius: 2, boxShadow: 2 }}>
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        mb: 3,
                    }}
                >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Button
                            variant="outlined"
                            size="small"
                            onClick={() => navigate('/form-builder')}
                        >
                            ← Back
                        </Button>

                        <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#333' }}>
                            ✏️ {formId && formId !== 'new' ? 'Edit' : 'Create'} Evaluation Form
                        </Typography>
                    </Box>
                </Box>

                <Grid container spacing={2} sx={{ mb: 3 }}>
                    <Grid item xs={12}>
                        <TextField
                            fullWidth
                            label="Form Title"
                            value={formTitle}
                            onChange={(e) => setFormTitle(e.target.value)}
                            placeholder="Enter form title"
                            variant="outlined"
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <TextField
                            fullWidth
                            label="Description"
                            value={formDescription}
                            onChange={(e) => setFormDescription(e.target.value)}
                            placeholder="Enter form description"
                            multiline
                            rows={3}
                            variant="outlined"
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <FormControl fullWidth>
                            <InputLabel>Status</InputLabel>
                            <Select
                                value={status}
                                onChange={(e) => setStatus(e.target.value)}
                                label="Status"
                            >
                                <MenuItem value="active">Active</MenuItem>
                                <MenuItem value="inactive">Inactive</MenuItem>
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={isPublished}
                                    onChange={(e) => setIsPublished(e.target.checked)}
                                    color="primary"
                                />
                            }
                            label={isPublished ? 'Published' : 'Unpublished'}
                        />
                    </Grid>
                </Grid>

                <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                    <Button
                        variant="outlined"
                        onClick={() => {
                            setJsonInput('');
                            setJsonDialogOpen(true);
                        }}
                    >
                        Import JSON
                    </Button>
                    <Button variant="outlined" onClick={() => navigate('/form-builder')}>
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        onClick={handleSaveForm}
                        disabled={loading}
                        sx={{ bgcolor: '#667eea' }}
                    >
                        {loading ? 'Saving...' : 'Save Form'}
                    </Button>
                </Box>
            </Paper>

            {/* Questions Section */}
            <Paper sx={{ p: { xs: 2, sm: 3 }, borderRadius: 2, boxShadow: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                    <Box>
                        <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#333' }}>
                            📋 Questions ({questions.length})
                        </Typography>
                        {questions.length > 0 && (
                            <Typography variant="body2" color="textSecondary">
                                Total Max Score: {calculateTotalMaxScore()} points
                            </Typography>
                        )}
                    </Box>
                    <Button
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={() => {
                            setNewQuestion({
                                text: '',
                                type: 'single-select',
                                mandatory: true,
                                maxScore: 10,
                                options: [{ text: '', score: 5 }],
                            });
                            setEditingQuestionIndex(null);
                            setDialogOpen(true);
                            setError('');
                        }}
                        sx={{ bgcolor: '#667eea' }}
                    >
                        Add Question
                    </Button>
                </Box>

                <Grid container spacing={2}>
                    {questions.map((question, index) => (
                        <Grid item xs={12} key={index}>
                            <Card sx={{ borderLeft: '4px solid #667eea' }}>
                                <CardContent>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                                        <Typography variant="subtitle1" sx={{ fontWeight: 'bold', flex: 1 }}>
                                            {index + 1}. {question.text}
                                        </Typography>
                                        {question.maxScore && (
                                            <Chip
                                                label={`Max: ${question.maxScore} pts`}
                                                color="primary"
                                                variant="outlined"
                                                size="small"
                                            />
                                        )}
                                    </Box>

                                    <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
                                        <Chip label={question.type} size="small" variant="outlined" />
                                        <Chip
                                            label={question.mandatory ? 'Mandatory' : 'Optional'}
                                            size="small"
                                            color={question.mandatory ? 'warning' : 'default'}
                                        />
                                    </Box>

                                    {question.type !== 'paragraph' && (
                                        <>
                                            <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1 }}>
                                                Options (Total: {question.options?.reduce((sum, opt) => sum + (opt.score || 0), 0)} pts max):
                                            </Typography>
                                            <Box component="ul" sx={{ pl: 2 }}>
                                                {question.options?.map((option, optIdx) => (
                                                    <Typography component="li" variant="body2" key={optIdx}>
                                                        {option.text} <strong>({option.score || 0} pts)</strong>
                                                    </Typography>
                                                ))}
                                            </Box>
                                        </>
                                    )}
                                </CardContent>
                                <CardActions>
                                    <Tooltip title="Edit">
                                        <IconButton
                                            size="small"
                                            onClick={() => handleEditQuestion(index)}
                                        >
                                            <EditIcon />
                                        </IconButton>
                                    </Tooltip>
                                    <Tooltip title="Delete">
                                        <IconButton
                                            size="small"
                                            onClick={() => handleDeleteQuestion(index)}
                                            color="error"
                                        >
                                            <DeleteIcon />
                                        </IconButton>
                                    </Tooltip>
                                </CardActions>
                            </Card>
                        </Grid>
                    ))}
                </Grid>

                {questions.length === 0 && (
                    <Typography color="textSecondary" sx={{ textAlign: 'center', py: 4 }}>
                        No questions added yet. Click "Add Question" to get started.
                    </Typography>
                )}
            </Paper>

            {/* Question Dialog */}
            <Dialog
                open={dialogOpen}
                onClose={() => setDialogOpen(false)}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>
                    {editingQuestionIndex !== null ? 'Edit Question' : 'Add Question'}
                </DialogTitle>
                <DialogContent dividers sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
                    <TextField
                        fullWidth
                        label="Question Text"
                        value={newQuestion.text}
                        onChange={(e) => setNewQuestion({ ...newQuestion, text: e.target.value })}
                        multiline
                        rows={2}
                        variant="outlined"
                    />

                    <FormControl fullWidth>
                        <InputLabel>Question Type</InputLabel>
                        <Select
                            value={newQuestion.type}
                            onChange={(e) => setNewQuestion({ ...newQuestion, type: e.target.value })}
                            label="Question Type"
                        >
                            <MenuItem value="single-select">Single Select</MenuItem>
                            <MenuItem value="mcq">Multiple Choice (MCQ)</MenuItem>
                            <MenuItem value="paragraph">Paragraph</MenuItem>
                        </Select>
                    </FormControl>

                    {/* ✅ NEW: Max Score Field */}
                    <TextField
                        fullWidth
                        label="Max Score for this Question"
                        type="number"
                        value={newQuestion.maxScore || 10}
                        onChange={(e) => setNewQuestion({ ...newQuestion, maxScore: parseInt(e.target.value) || 0 })}
                        variant="outlined"
                        inputProps={{ min: 1 }}
                    />

                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={newQuestion.mandatory}
                                onChange={(e) =>
                                    setNewQuestion({ ...newQuestion, mandatory: e.target.checked })
                                }
                            />
                        }
                        label="Mandatory"
                    />

                    {/* Options */}
                    {newQuestion.type !== 'paragraph' && (
                        <Box>
                            <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 2 }}>
                                Options (Enter score for each)
                            </Typography>
                            {newQuestion.options?.map((option, idx) => (
                                <Box key={idx} sx={{ display: 'flex', gap: 1, mb: 2, alignItems: 'flex-start' }}>
                                    <TextField
                                        fullWidth
                                        label="Option Text"
                                        value={option.text}
                                        onChange={(e) =>
                                            handleOptionChange(idx, 'text', e.target.value)
                                        }
                                        size="small"
                                        variant="outlined"
                                    />
                                    <TextField
                                        label="Score"
                                        type="number"
                                        value={option.score || 0}
                                        onChange={(e) =>
                                            handleOptionChange(idx, 'score', e.target.value)
                                        }
                                        size="small"
                                        variant="outlined"
                                        sx={{ width: 100 }}
                                        inputProps={{ min: 0 }}
                                    />
                                    <IconButton
                                        size="small"
                                        onClick={() => handleDeleteOption(idx)}
                                        color="error"
                                        disabled={newQuestion.options.length === 1}
                                        title="Delete Option"
                                    >
                                        <DeleteIcon />
                                    </IconButton>
                                </Box>
                            ))}
                            <Button
                                variant="outlined"
                                startIcon={<AddIcon />}
                                onClick={handleAddOption}
                                fullWidth
                            >
                                Add Option
                            </Button>
                        </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => {
                        setDialogOpen(false);
                        setError('');
                    }}>
                        Cancel
                    </Button>
                    <Button
                        onClick={handleAddQuestion}
                        variant="contained"
                        disabled={!newQuestion.text}
                        sx={{ bgcolor: '#667eea' }}
                    >
                        {editingQuestionIndex !== null ? 'Update' : 'Add'}
                    </Button>
                </DialogActions>
            </Dialog>
            <Dialog
                open={jsonDialogOpen}
                onClose={() => setJsonDialogOpen(false)}
                maxWidth="md"
                fullWidth
            >
                <DialogTitle>Import Evaluation Form JSON</DialogTitle>

                <DialogContent dividers>
                    <TextField
                        multiline
                        minRows={12}
                        fullWidth
                        placeholder="Paste evaluation form JSON here"
                        value={jsonInput}
                        onChange={(e) => setJsonInput(e.target.value)}
                    />
                </DialogContent>

                <DialogActions>
                    <Button onClick={() => setJsonDialogOpen(false)}>
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        onClick={handleImportJson}
                        sx={{ bgcolor: '#667eea' }}
                    >
                        Import
                    </Button>
                </DialogActions>
            </Dialog>

            {/* <Box sx={{ height: 3000, bgcolor: 'red' }} /> */}
        </Container>
    );

}
